<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { Trash2 } from "lucide-vue-next";

const props = withDefaults(
  defineProps<{
    open: boolean;
    kind: "comment" | "file";
    name: string;
    deleting?: boolean;
    error?: string;
  }>(),
  { deleting: false, error: "" },
);
const emit = defineEmits<{ cancel: []; confirm: [] }>();
const confirmation = ref("");
const dialog = ref<HTMLFormElement | null>(null);
const confirmationInput = ref<HTMLInputElement | null>(null);
let opener: HTMLElement | null = null;
let priorBodyOverflow = "";

const cancel = () => {
  if (!props.deleting) emit("cancel");
};
const confirm = () => {
  if (confirmation.value === "delete" && !props.deleting) emit("confirm");
};
function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    cancel();
    return;
  }
  if (event.key !== "Tab" || !dialog.value) return;
  const controls = [
    ...dialog.value.querySelectorAll<HTMLElement>(
      "input:not([disabled]), button:not([disabled])",
    ),
  ];
  if (!controls.length) return;
  if (event.shiftKey && document.activeElement === controls[0]) {
    event.preventDefault();
    controls.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === controls.at(-1)) {
    event.preventDefault();
    controls[0]?.focus();
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      confirmation.value = "";
      opener = document.activeElement as HTMLElement;
      priorBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      confirmationInput.value?.focus();
      return;
    }
    document.body.style.overflow = priorBodyOverflow;
    opener?.focus();
    opener = null;
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  if (props.open) document.body.style.overflow = priorBodyOverflow;
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="artifact-delete-layer">
      <button
        class="artifact-delete-scrim"
        type="button"
        :aria-label="`Keep ${kind}`"
        :disabled="deleting"
        @click="cancel"
      ></button>
      <form
        ref="dialog"
        class="artifact-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="artifact-delete-title"
        aria-describedby="artifact-delete-description"
        @keydown="trapKeys"
        @submit.prevent="confirm"
      >
        <header>
          <span class="artifact-delete-dialog__icon" aria-hidden="true">
            <Trash2 :size="20" />
          </span>
          <div>
            <h2 id="artifact-delete-title">
              Delete {{ kind === "file" ? "file" : "comment" }}?
            </h2>
            <p id="artifact-delete-description">
              <template v-if="kind === 'file'">
                This removes the file from the project and prevents future
                downloads. This cannot be undone.
              </template>
              <template v-else>
                This removes the comment, its attached files, and pending
                notifications. Replies remain with a deleted-comment marker when
                needed to preserve the thread. This cannot be undone.
              </template>
            </p>
          </div>
        </header>
        <div class="artifact-delete-dialog__body">
          <strong class="artifact-delete-dialog__name">{{ name }}</strong>
          <label class="field" for="artifact-delete-confirmation">
            <span>Type <strong>delete</strong> to confirm</span>
            <input
              id="artifact-delete-confirmation"
              ref="confirmationInput"
              v-model="confirmation"
              class="input"
              type="text"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              :disabled="deleting"
            />
          </label>
          <p v-if="error" class="artifact-delete-dialog__error" role="alert">
            {{ error }}
          </p>
        </div>
        <footer>
          <button
            class="button button--secondary"
            type="button"
            :disabled="deleting"
            @click="cancel"
          >
            Keep {{ kind }}
          </button>
          <button
            class="button button--danger"
            type="submit"
            :disabled="confirmation !== 'delete' || deleting"
          >
            {{ deleting ? "Deleting…" : `Delete ${kind}` }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
.artifact-delete-layer {
  position: fixed;
  inset: 0;
  z-index: 190;
  display: grid;
  place-items: center;
  padding: 20px;
}
.artifact-delete-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(13, 35, 50, 0.58);
}
.artifact-delete-dialog {
  position: relative;
  width: min(520px, 100%);
  max-height: calc(100vh - 40px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.artifact-delete-dialog > header {
  display: flex;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--rule);
}
.artifact-delete-dialog h2 {
  margin: 0 0 6px;
  color: var(--ink-900);
  font-size: 20px;
}
.artifact-delete-dialog header p {
  margin: 0;
  color: var(--text-soft);
}
.artifact-delete-dialog__icon {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: var(--radius-md);
  background: var(--danger-soft);
  color: var(--danger);
}
.artifact-delete-dialog__body {
  min-height: 0;
  display: grid;
  gap: 18px;
  overflow: auto;
  padding: 18px 24px 22px;
}
.artifact-delete-dialog__name {
  overflow-wrap: anywhere;
}
.artifact-delete-dialog .field {
  display: grid;
  gap: 6px;
}
.artifact-delete-dialog__error {
  padding: 9px 10px;
  border-radius: var(--radius-sm);
  background: var(--danger-soft);
  color: var(--danger);
}
.artifact-delete-dialog > footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
@media (max-width: 560px) {
  .artifact-delete-layer {
    align-items: end;
    padding: 0;
  }
  .artifact-delete-dialog {
    width: 100%;
    max-height: calc(100vh - 20px);
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  .artifact-delete-dialog > header,
  .artifact-delete-dialog__body {
    padding-inline: 18px;
  }
  .artifact-delete-dialog > footer {
    padding-inline: 18px;
  }
}
</style>
